'use client';

import { BaseDefiYieldOverviewResponse } from '@flipster/apis/v1/dynamic-earn/types';
import { FormattedMessage } from '@flipster/i18n';
import { useRouter } from '@flipster/i18n/routing';
import { Button } from '@flipster/react/design-system/button';
import { Tooltip } from '@flipster/react/design-system/tooltip';
import Formatted from '@flipster/react/design-utils/formatted';
import ChevronRightIcon from '@flipster/react/icon/generated/ChevronRightIcon';
import InformationCircleIcon from '@flipster/react/icon/generated/InformationCircleIcon';
import { css } from '@flipster/styled-system/css';
import { grid, hstack, square, stack } from '@flipster/styled-system/patterns';
import type { BigNumber } from '@flipster/utils/num';
import segment from '@flipster/utils/segment';
import { DynamicEarnEvent } from '@flipster/utils/segment/events/dynamic-earn';

import { CurrencyDisplay } from '../../../../misc/display';
import { getNumberColorWithNegativeWhite } from '../../../shared/color';
import { CrossMarginTooltipContent } from '../../../shared/cross-margin-tooltip-content';

export interface InactiveCardProps {
  overview: BaseDefiYieldOverviewResponse | undefined;
  currency: string;
  productName: string;
  detailsPath: string;
  tvl: string | undefined;
  collateralRate: BigNumber | undefined;
  onExplore: () => void;
  onAprInfoClick: () => void;
}

export const InactiveCard = ({
  overview,
  currency,
  productName,
  detailsPath,
  tvl,
  collateralRate,
  onExplore,
  onAprInfoClick,
}: InactiveCardProps) => {
  const router = useRouter();

  return (
    <div
      className={grid({
        rounded: 8,
        bg: 'background.default.level-1',
        p: { base: 16, tablet: 24 },
        gap: { base: 16, tablet: 24 },
        gridTemplateColumns: { base: '1fr', tablet: 'auto 1fr' },
        alignItems: 'center',
      })}
    >
      <video
        playsInline
        autoPlay
        muted
        loop
        src={overview?.shortVideoUrl ?? undefined}
        className={css({
          cursor: 'pointer',
          w: { base: '100%', tablet: 248 },
          h: { tablet: 139 },
          tabletDown: { aspectRatio: '248 / 139' },
          objectFit: 'cover',
          rounded: 8,
        })}
        onClick={() => {
          segment.track(DynamicEarnEvent.DynamicEarnProductDetailPageClick, {
            button_type: 'video',
          });
          router.push(detailsPath);
        }}
      >
        <track kind='captions' />
      </video>
      <div className={stack({ h: '100%', justify: 'space-between', gap: 16 })}>
        <button
          type='button'
          onClick={() => {
            segment.track(DynamicEarnEvent.DynamicEarnProductDetailPageClick, {
              button_type: productName.toLowerCase().replace(/\s+/g, '_'),
            });
            router.push(detailsPath);
          }}
          className={hstack({ alignSelf: 'start' })}
        >
          <CurrencyDisplay
            currency={currency}
            icon={<CurrencyDisplay.Icon size={32} />}
            mainLabel={<CurrencyDisplay.Label value='fullName' css={{ textStyle: 'Text/L/Regular' }} />}
            css={{ color: 'content.default.level-5' }}
          />
          <div className={square({ size: 24, color: 'content.default.level-3' })}>
            <ChevronRightIcon size={16} />
          </div>
        </button>
        <div
          className={css({
            display: 'flex',
            flexDirection: { tabletDown: 'column', tablet: 'row' },
            gap: { tabletDown: 16, tablet: 32 },
            pb: { tabletDown: 0, tablet: 20 },
          })}
        >
          <div className={stack({ gap: 4, tablet: { flex: 1 } })}>
            <div className={hstack({ gap: 2 })}>
              <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-2' })}>
                <FormattedMessage id='Common.r251209.Apr' />
              </p>
              <button
                type='button'
                className={square({ size: 24, color: 'content.default.level-1' })}
                onClick={onAprInfoClick}
              >
                <InformationCircleIcon size={16} />
              </button>
            </div>
            <div className={hstack({ gap: 4 })}>
              <Formatted.Percent
                key='percent'
                value={overview?.estimateApr}
                isRate
                precision={2}
                toFixed
                css={{
                  textStyle: 'Title/M/Monospace',
                  color: getNumberColorWithNegativeWhite(overview?.estimateApr),
                }}
              />
            </div>
          </div>

          <div className={stack({ gap: 4 })}>
            <div className={hstack({ gap: 4 })}>
              <Tooltip.Root placement='right' maxWidth={280}>
                <Tooltip.TextTrigger label='TVL' />
                <Tooltip.Content bottomSheetTitle='TVL'>
                  <FormattedMessage id='Earn.r251208.The_total_amount_subscribed_product' />
                </Tooltip.Content>
              </Tooltip.Root>
              <span className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-2' })}>(USDT)</span>
            </div>
            <p className={css({ textStyle: 'Title/M/Monospace', color: 'content.state.positive' })}>
              <Formatted.ShortNumber value={tvl} />
            </p>
          </div>

          <div className={stack({ gap: 4 })}>
            <Tooltip.Root placement='right' maxWidth={280}>
              <Tooltip.TextTrigger
                label={<FormattedMessage id='Earn.r260203.Available_for_cross_margin' />}
                css={{ textStyle: 'Text/M/Regular' }}
              />
              <Tooltip.Content bottomSheetTitle={<FormattedMessage id='Earn.r260203.Available_for_cross_margin' />}>
                <CrossMarginTooltipContent collateralRate={collateralRate} />
              </Tooltip.Content>
            </Tooltip.Root>
            <p
              className={css({
                textStyle: 'Title/M/Monospace',
                color: getNumberColorWithNegativeWhite(collateralRate),
              })}
            >
              <Formatted.Percent key='percent' value={collateralRate} isRate precision={2} />
            </p>
          </div>

          <Button
            size='large'
            label={<FormattedMessage id='Earn.r260203.Earn_while_trading' />}
            css={{ tabletDown: { w: '100%' } }}
            onClick={() => {
              segment.track(DynamicEarnEvent.DynamicEarnExploreClick, {
                product_name: productName,
                apr: overview?.estimateApr ?? '0',
              });
              onExplore();
            }}
          />
        </div>
      </div>
    </div>
  );
};
